import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPOById, acceptPO, rejectPO, exportPOToPDF, addPOComment } from '../../store/poSlice';
import { RootState, AppDispatch } from '../../store';
import {
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  ChatBubbleLeftIcon,
} from '@heroicons/react/24/outline';

const PODetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { currentPO, loading } = useSelector((state: RootState) => state.purchaseOrders);
  const { user } = useSelector((state: RootState) => state.auth);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [newComment, setNewComment] = useState('');

  useEffect(() => {
    if (id) {
      dispatch(fetchPOById(id));
    }
  }, [dispatch, id]);

  const handleAccept = async () => {
    if (id) {
      await dispatch(acceptPO({ id, notes }));
      setShowAcceptModal(false);
      setNotes('');
    }
  };

  const handleReject = async () => {
    if (id && rejectReason) {
      await dispatch(rejectPO({ id, reason: rejectReason }));
      setShowRejectModal(false);
      setRejectReason('');
    }
  };

  const handleExportPDF = async () => {
    if (id) {
      await dispatch(exportPOToPDF(id));
    }
  };

  const handleAddComment = async () => {
    if (id && newComment.trim()) {
      await dispatch(addPOComment({ id, text: newComment }));
      setNewComment('');
    }
  };

  if (loading || !currentPO) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Check if user's company is the supplier for this PO and status is pending
  const userCompanyId = user?.companyId || user?.company?._id || user?.company;
  const supplierCompanyId = currentPO.supplierId?._id || currentPO.supplierId;
  const isSupplierForThisPO = userCompanyId === supplierCompanyId;
  const canAcceptReject = isSupplierForThisPO &&
                          (currentPO.status === 'pending' || currentPO.status === 'PENDING');

  // Debug logging (remove in production)
  console.log('Accept/Reject Debug:', {
    user,
    userCompanyId,
    supplierCompanyId,
    isSupplierForThisPO,
    poStatus: currentPO.status,
    canAcceptReject,
    userRole: user?.role
  });

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/purchase-orders')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Purchase Orders
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{currentPO.poNumber}</h1>
            <p className="mt-1 text-sm text-gray-600">
              Created on {new Date(currentPO.createdAt).toLocaleDateString()}
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleExportPDF}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
              Export PDF
            </button>
            {canAcceptReject && (
              <>
                <button
                  onClick={() => setShowAcceptModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  Accept
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  <XCircleIcon className="h-5 w-5 mr-2" />
                  Reject
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Debug Info Banner - Remove in production */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">Debug Information:</h3>
        <div className="text-xs text-blue-700 space-y-1">
          <p>Your Company ID: {userCompanyId || 'Not set'}</p>
          <p>Supplier Company ID: {supplierCompanyId || 'Not set'}</p>
          <p>Your Role: {user?.role || 'Not set'}</p>
          <p>PO Status: {currentPO.status || 'Not set'}</p>
          <p>Is Supplier for this PO: {isSupplierForThisPO ? 'Yes ✓' : 'No ✗'}</p>
          <p>Status is Pending: {(currentPO.status === 'pending' || currentPO.status === 'PENDING') ? 'Yes ✓' : 'No ✗'}</p>
          <p className="font-semibold">Can Accept/Reject: {canAcceptReject ? 'Yes ✓' : 'No ✗'}</p>
          <p className="mt-2 font-medium">
            {!isSupplierForThisPO && '⚠️ You are not the supplier for this order.'}
            {isSupplierForThisPO && currentPO.status !== 'pending' && currentPO.status !== 'PENDING' && '⚠️ This order is not in pending status.'}
            {canAcceptReject && '✅ Accept/Reject buttons should be visible!'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Buyer</p>
                <p className="mt-1 text-sm font-medium text-gray-900">
                  {currentPO.buyerId?.name}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Supplier</p>
                <p className="mt-1 text-sm font-medium text-gray-900">
                  {currentPO.supplierId?.name}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className="mt-1">
                  <span className="px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                    {currentPO.status?.toUpperCase()}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Amount</p>
                <p className="mt-1 text-sm font-medium text-gray-900">
                  ${currentPO.totalAmount?.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Delivery Address</p>
                <p className="mt-1 text-sm font-medium text-gray-900">
                  {currentPO.deliveryAddress || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Expected Delivery</p>
                <p className="mt-1 text-sm font-medium text-gray-900">
                  {currentPO.expectedDeliveryDate
                    ? new Date(currentPO.expectedDeliveryDate).toLocaleDateString()
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Items</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Item Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Unit Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentPO.items?.map((item: any, index: number) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.itemName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.itemCode}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.quantity} {item.unit}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${item.unitPrice}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ${(item.quantity * item.unitPrice).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes */}
          {currentPO.notes && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
              <p className="text-sm text-gray-700">{currentPO.notes}</p>
            </div>
          )}

          {/* Comments Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <ChatBubbleLeftIcon className="h-5 w-5 mr-2 text-gray-500" />
              Comments
            </h2>

            {/* Existing Comments */}
            <div className="space-y-4 mb-6">
              {currentPO.comments && currentPO.comments.length > 0 ? (
                currentPO.comments.map((comment: any, index: number) => (
                  <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                    <p className="text-sm text-gray-700">{comment.text}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {comment.userId?.firstName && comment.userId?.lastName
                        ? `${comment.userId.firstName} ${comment.userId.lastName}`
                        : 'User'} • {new Date(comment.createdAt).toLocaleString()}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No comments yet</p>
              )}
            </div>

            {/* Add New Comment */}
            <div className="border-t pt-4">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={3}
              />
              <div className="mt-3 flex justify-end">
                <button
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || loading}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChatBubbleLeftIcon className="h-4 w-4 mr-2" />
                  Add Comment
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Documents */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Documents</h2>
            {currentPO.documents && currentPO.documents.length > 0 ? (
              <div className="space-y-2">
                {currentPO.documents.map((doc: string, index: number) => (
                  <a
                    key={index}
                    href={doc}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <span className="text-sm text-gray-700">Document {index + 1}</span>
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No documents attached</p>
            )}
          </div>

          {/* Activity Timeline */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Activity</h2>
            <div className="space-y-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <DocumentTextIcon className="h-4 w-4 text-blue-600" />
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">PO Created</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(currentPO.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Accept Modal */}
      {showAcceptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Accept Purchase Order</h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes (optional)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 mb-4"
              rows={4}
            />
            <div className="flex space-x-3">
              <button
                onClick={handleAccept}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Confirm Accept
              </button>
              <button
                onClick={() => setShowAcceptModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Purchase Order</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection (required)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 mb-4"
              rows={4}
              required
            />
            <div className="flex space-x-3">
              <button
                onClick={handleReject}
                disabled={!rejectReason}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Confirm Reject
              </button>
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PODetails;